import { Alert, Card, Col, Empty, Row, Segmented, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Line } from '@ant-design/plots';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../../api/client';
import { errMsg } from '../../lib/errors';
import { ListenerName } from '../../lib/listenerName';
import { oneOf, useUrlState } from '../../lib/urlState';

/**
 * Call delivery health — are incoming-call rings reaching listeners' phones?
 * Backed by GET /admin/analytics/call-delivery (maja_api
 * call-delivery-health.service.ts; docs/incoming-call-reliability/12).
 *
 * "Delivered" means the PHONE confirmed it (socket at ring time, or its
 * ring-status acknowledgement) — never just that Google accepted the push.
 */

export interface DeliveryAlert {
  key: string;
  severity: 'critical' | 'warning';
  title: string;
  detail: string;
  value: number;
  threshold: number;
  windowMin: number;
}

export interface DeliveryHealthReport {
  window: { hours: number; from: string; to: string };
  rings: {
    rings: number;
    delivered: number;
    deliveredBySocket: number;
    deliveredByAck: number;
    deliveryRate: number | null;
    /** Rings with evidence they were on screen (native ring posted, the app's
     *  fallback ring, in-app ring in front, or the listener acted). Absent on
     *  an API older than presentation tracking. */
    presented?: number;
    presentedRate?: number | null;
    /** Delivered only by a live socket, never shown as far as we know. */
    socketUnconfirmed?: number;
    accepted: number;
    connected: number;
    answerRate: number | null;
    undeliveredTimeouts: number;
    callerCancelledBeforeDelivery: number;
    lateAccepts: number;
    inFlight: number;
  };
  latencyMs: { nativeP50: number | null; nativeP95: number | null; deliveryP50: number | null; deliveryP95: number | null };
  nativeDecisions: { decision: string; count: number }[];
  phoneSettings: {
    withEnv: number;
    fullScreenOff: number;
    notificationsOff: number;
    channelLowered: number;
    channelMissing: number;
    doNotDisturb: number;
    byOem: { oem: string; rings: number; fullScreenOff: number }[];
  };
  push: { sent: number; failed: number; noDevices: number; providerErrors: number; byError: { code: string; count: number }[] };
  notCallable: { reason: string; count: number }[];
  sessionEnds: { reason: string; count: number }[];
  hourly: { hour: string; rings: number; delivered: number; connected: number }[];
  /** First presentation record ever (null: none yet; absent: older API). */
  presentationTrackedSince?: string | null;
  problemListeners: {
    listenerId: string;
    displayName: string;
    handle: string | null;
    rings: number;
    undelivered: number;
    lastDeliveredAt: string | null;
    lastFullScreen: boolean | null;
    lastNotifications: boolean | null;
    lastOem: string | null;
  }[];
  alerts: DeliveryAlert[];
}

const WINDOWS = ['1', '6', '24', '72', '168'] as const;
const WINDOW_LABEL: Record<(typeof WINDOWS)[number], string> = {
  '1': '1 h',
  '6': '6 h',
  '24': '24 h',
  '72': '3 days',
  '168': '7 days',
};

/** What the phone's native receiver did with the push (DudeFcmReceiver). */
export const DECISION_LABEL: Record<string, string> = {
  RING: 'Rang natively (lock screen / background)',
  SKIP_APP_IN_FRONT: 'App was on screen — rang in-app',
  SKIP_RECEPTION_OFF: "Listener's call-reception switch was off",
  SKIP_EXPIRED: 'Arrived too late to answer — not shown',
  SKIP_NOT_ENABLED: 'Native ring off — Flutter rang it',
  none: 'Socket ring or older app build (no native report)',
};

const NOT_CALLABLE_LABEL: Record<string, string> = {
  undelivered_rings: 'Rings stopped reaching the phone',
  no_usable_push_token: 'No working push token left',
};

/** "24 Oct 14:00" in the admin's local time. */
const hourLabel = (iso: string) =>
  new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const ms = (v: number | null) => (v === null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`);

function Hint({ title, text }: { title: string; text: string }) {
  return (
    <Space size={4}>
      {title}
      <Tooltip title={text}>
        <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
      </Tooltip>
    </Space>
  );
}

export function CallDeliveryHealthPage() {
  const { get, set } = useUrlState();
  const hours = oneOf(get('hours'), WINDOWS, '24') ?? '24';
  const q = useQuery({
    queryKey: ['admin', 'analytics', 'call-delivery', hours],
    queryFn: async () =>
      (await api.get<DeliveryHealthReport>('/admin/analytics/call-delivery', { params: { hours } })).data,
    refetchInterval: 60_000,
  });
  const r = q.data;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%', flexWrap: 'wrap' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Call delivery
          </Typography.Title>
          <Typography.Text type="secondary">
            Are incoming calls reaching listeners&apos; phones? Delivered = the phone confirmed it, not just that the push was sent.
          </Typography.Text>
        </div>
        <Segmented
          aria-label="Time window"
          value={hours}
          options={WINDOWS.map((w) => ({ label: WINDOW_LABEL[w], value: w }))}
          onChange={(v) => set({ hours: v === '24' ? null : String(v) })}
        />
      </Space>

      {q.isError && <Alert type="error" showIcon message="Could not load call delivery" description={errMsg(q.error)} />}

      {r && r.alerts.length === 0 && (
        <Alert type="success" showIcon message="No delivery alerts right now" data-testid="delivery-no-alerts" />
      )}
      {r?.alerts.map((a) => (
        <Alert
          key={a.key}
          type={a.severity === 'critical' ? 'error' : 'warning'}
          showIcon
          message={a.title}
          description={a.detail}
          data-testid={`delivery-alert-${a.key}`}
        />
      ))}

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card loading={q.isLoading}>
            <Statistic
              title={<Hint title="Delivery rate" text="Rings that reached the app (a connected socket when the ring went out, or the phone's ring acknowledgement) ÷ rings started. A connected socket is NOT proof the ring was shown — see 'Shown on screen'. Rings from the last 30 s are still in flight and not counted." />}
              value={pct(r?.rings.deliveryRate ?? null)}
              suffix={r ? <Typography.Text type="secondary" style={{ fontSize: 14 }}>{`${r.rings.delivered} / ${r.rings.rings}`}</Typography.Text> : null}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={q.isLoading}>
            <Statistic
              title={<Hint title="Answer rate" text="Connected calls ÷ delivered rings — what listeners did with rings that actually reached them." />}
              value={pct(r?.rings.answerRate ?? null)}
              suffix={r ? <Typography.Text type="secondary" style={{ fontSize: 14 }}>{`${r.rings.connected} connected`}</Typography.Text> : null}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={q.isLoading}>
            <Statistic
              title={<Hint title="Never reached the phone" text="Rings that timed out with no delivery evidence. Caller hang-ups before delivery are shown separately and are not counted here." />}
              value={r?.rings.undeliveredTimeouts ?? 0}
              suffix={r ? <Typography.Text type="secondary" style={{ fontSize: 14 }}>{`+ ${r.rings.callerCancelledBeforeDelivery} caller hung up first`}</Typography.Text> : null}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={q.isLoading}>
            <Statistic
              title={<Hint title="Ring time (median / p95)" text="Push arrival → ring on screen, measured on the phone (native-first builds). Server-side delivery latency is in the second line." />}
              value={r ? `${ms(r.latencyMs.nativeP50)} / ${ms(r.latencyMs.nativeP95)}` : '—'}
            />
            {r && (
              <Typography.Text type="secondary">
                {`Delivery ${ms(r.latencyMs.deliveryP50)} / ${ms(r.latencyMs.deliveryP95)} · late accepts ${r.rings.lateAccepts}`}
              </Typography.Text>
            )}
          </Card>
        </Col>
      </Row>

      {r?.rings.presented !== undefined && (
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card loading={q.isLoading}>
              <Statistic
                title={<Hint title="Shown on screen" text="Rings with evidence they were displayed: the phone posted the native ring, the app posted its fallback ring, the app's own ringing screen was in front, or the listener accepted / declined. Proves the phone showed it — not that a person looked." />}
                value={pct(r.rings.presentedRate ?? null)}
                suffix={<Typography.Text type="secondary" style={{ fontSize: 14 }}>{`${r.rings.presented} / ${r.rings.rings}`}</Typography.Text>}
                valueStyle={{ fontSize: 22 }}
                data-testid="delivery-presented"
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card loading={q.isLoading}>
              <Statistic
                title={<Hint title="Socket only — not confirmed" text="Counted as delivered because the app had a connected socket, but nothing shows the ring was ever on screen (e.g. a minimised app whose push was late). Older app builds never report presentation, so all their socket rings land here." />}
                value={r.rings.socketUnconfirmed ?? 0}
                valueStyle={{ fontSize: 22 }}
                data-testid="delivery-socket-unconfirmed"
              />
              <Typography.Text type="secondary">
                {r.presentationTrackedSince
                  ? `Tracked since ${new Date(r.presentationTrackedSince).toLocaleString()}`
                  : 'No presentation reports yet — needs the new app build'}
              </Typography.Text>
            </Card>
          </Col>
        </Row>
      )}

      <Card title="Rings per hour" loading={q.isLoading}>
        {r && r.hourly.length > 0 ? (
          <Line
            height={240}
            data={r.hourly.flatMap((h) => {
              const time = hourLabel(h.hour);
              return [
                { time, series: 'Rings', value: h.rings },
                { time, series: 'Delivered', value: h.delivered },
                { time, series: 'Connected', value: h.connected },
              ];
            })}
            xField="time"
            yField="value"
            colorField="series"
          />
        ) : (
          <Empty description="No rings in this window" />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<Hint title="What the phone did" text="The native receiver's decision for each delivered ring (newer app builds report it)." />} loading={q.isLoading}>
            <Table
              size="small"
              pagination={false}
              rowKey="decision"
              dataSource={r?.nativeDecisions ?? []}
              columns={[
                { title: 'Decision', dataIndex: 'decision', render: (d: string) => DECISION_LABEL[d] ?? d },
                { title: 'Rings', dataIndex: 'count', align: 'right' },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<Hint title="Phone settings at ring time" text="From builds that report them. A locked phone without the full-screen permission shows only a notification; Do Not Disturb and a lowered channel can silence the ring." />}
            loading={q.isLoading}
          >
            {r && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text type="secondary">{`${r.phoneSettings.withEnv} delivered rings reported phone settings`}</Typography.Text>
                <Space wrap>
                  <Tag color={r.phoneSettings.fullScreenOff ? 'red' : 'default'}>{`Full-screen off: ${r.phoneSettings.fullScreenOff}`}</Tag>
                  <Tag color={r.phoneSettings.notificationsOff ? 'red' : 'default'}>{`Notifications off: ${r.phoneSettings.notificationsOff}`}</Tag>
                  <Tag color={r.phoneSettings.channelLowered ? 'orange' : 'default'}>{`Call channel lowered: ${r.phoneSettings.channelLowered}`}</Tag>
                  <Tag>{`Channel not created yet: ${r.phoneSettings.channelMissing}`}</Tag>
                  <Tag color={r.phoneSettings.doNotDisturb ? 'orange' : 'default'}>{`Do Not Disturb: ${r.phoneSettings.doNotDisturb}`}</Tag>
                </Space>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="oem"
                  dataSource={r.phoneSettings.byOem}
                  columns={[
                    { title: 'Phone maker', dataIndex: 'oem' },
                    { title: 'Rings', dataIndex: 'rings', align: 'right' },
                    { title: 'Full-screen off', dataIndex: 'fullScreenOff', align: 'right' },
                  ]}
                />
              </Space>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<Hint title="Incoming-call pushes" text="What Google's FCM said. A push FCM accepted can still never reach a force-stopped or offline phone — see delivery rate for what phones confirmed." />} loading={q.isLoading}>
            {r && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space wrap>
                  <Tag color="green">{`Sent: ${r.push.sent}`}</Tag>
                  <Tag color={r.push.failed ? 'red' : 'default'}>{`Failed: ${r.push.failed}`}</Tag>
                  <Tooltip title="The listener had no push token; the ring could only go over the open app's socket.">
                    <Tag>{`No device: ${r.push.noDevices}`}</Tag>
                  </Tooltip>
                  <Tooltip title="Individual FCM attempts that errored (retried automatically).">
                    <Tag>{`Provider errors: ${r.push.providerErrors}`}</Tag>
                  </Tooltip>
                </Space>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="code"
                  dataSource={r.push.byError}
                  columns={[
                    { title: 'Error', dataIndex: 'code' },
                    { title: 'Count', dataIndex: 'count', align: 'right' },
                  ]}
                />
              </Space>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<Hint title="Taken out of rotation" text="Listeners the server stopped offering to callers, and why their callable time ended." />} loading={q.isLoading}>
            {r && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="reason"
                  dataSource={r.notCallable}
                  locale={{ emptyText: 'None' }}
                  columns={[
                    { title: 'Not callable because', dataIndex: 'reason', render: (v: string) => NOT_CALLABLE_LABEL[v] ?? v },
                    { title: 'Times', dataIndex: 'count', align: 'right' },
                  ]}
                />
                <Table
                  size="small"
                  pagination={false}
                  rowKey="reason"
                  dataSource={r.sessionEnds}
                  locale={{ emptyText: 'None' }}
                  columns={[
                    { title: 'Online session ended', dataIndex: 'reason' },
                    { title: 'Sessions', dataIndex: 'count', align: 'right' },
                  ]}
                />
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title={<Hint title="Listeners whose rings most often never arrived" text="The people to contact: battery / autostart settings, full-screen permission, or a reinstall. Settings shown are from their last delivered ring." />}
        loading={q.isLoading}
      >
        <Table<DeliveryHealthReport['problemListeners'][number]>
          size="small"
          rowKey="listenerId"
          pagination={false}
          dataSource={r?.problemListeners ?? []}
          locale={{ emptyText: 'No undelivered rings in this window' }}
          columns={problemColumns}
        />
      </Card>
    </Space>
  );
}

const yesNo = (v: boolean | null, bad: string) =>
  v === null ? <Typography.Text type="secondary">—</Typography.Text> : v ? 'On' : <Tag color="red">{bad}</Tag>;

const problemColumns: ColumnsType<DeliveryHealthReport['problemListeners'][number]> = [
  {
    title: 'Listener',
    render: (_: unknown, p) => <ListenerName listener={{ displayName: p.displayName, handle: p.handle }} listenerId={p.listenerId} />,
  },
  { title: 'Rings', dataIndex: 'rings', align: 'right' },
  { title: 'Never arrived', dataIndex: 'undelivered', align: 'right' },
  {
    title: 'Last delivered',
    dataIndex: 'lastDeliveredAt',
    render: (v: string | null) => (v ? new Date(v).toLocaleString() : <Typography.Text type="secondary">never</Typography.Text>),
  },
  { title: 'Full-screen', dataIndex: 'lastFullScreen', render: (v: boolean | null) => yesNo(v, 'Off') },
  { title: 'Notifications', dataIndex: 'lastNotifications', render: (v: boolean | null) => yesNo(v, 'Off') },
  { title: 'Phone', dataIndex: 'lastOem', render: (v: string | null) => v ?? '—' },
];
